import { Alert, Button, Modal, Popconfirm, Typography } from "antd";
import { useCompanySupportAccess } from "hooks/useCompanySupportAccess";
import { useState } from "react";
import { useCompanyStore } from "store/company.store";
import { api } from "utils/axios";
import { getActAsGrantId } from "utils/supportAccess";
import { supportAccessCopy } from "./supportAccessCopy";

/**
 * The CUSTOMER's half of support access, in the app shell.
 *
 * 🔑 Why this exists at all: the approval copy tells the customer *"you can end
 * this at any time"*, and an earlier version of this design had no UI behind
 * that promise anywhere. Staff got three layers of protection and the customer
 * got a receipt afterwards. A promise with no control behind it is worse than
 * not making it.
 */
export function CustomerSupportAccessBar() {
	const companyId = useCompanyStore((s) => s.id);
	const { activeGrants, refresh } = useCompanySupportAccess(
		companyId ?? undefined,
	);

	if (!companyId) return null;

	/*
	 * 🔑 NOT IN THE OPERATOR'S OWN BROWSER. Inside a session `req.user` IS the
	 * subject, so this company's "a session is live" endpoint answers yes to the
	 * staff member too — and they got two stacked banners saying the same thing
	 * in two colours, the second of which addresses them as the customer and
	 * offers them a button to end their own access under someone else's name.
	 *
	 * Keyed on the LOCAL act-as id rather than on the server echo: it is
	 * synchronous, so the customer bar never flashes on for the render before
	 * the echo lands. A stale id here can only ever HIDE this bar, and the tab
	 * that has one is already showing the staff bar's mismatch alarm, which is
	 * louder than anything this bar says.
	 */
	if (getActAsGrantId()) return null;

	/**
	 * NOTHING RENDERS WHEN NO SESSION IS KNOWN — including when the lookup
	 * failed.
	 *
	 * An earlier version treated "could not ask" as its own loud state, on the
	 * reasoning that `activeGrants` collapses both outcomes to `[]` and silence
	 * would tell the customer "nobody is here" on the strength of a request that
	 * never answered. In practice the lookup fails routinely — it is also
	 * `isLoading` on every first paint — so the warning was on screen constantly
	 * for accounts with no support session at all. A banner that is almost always
	 * wrong trains the customer to ignore the one that is right.
	 *
	 * The staff side keeps its `unconfirmed` alarm: there the tab is holding an
	 * act-as id, so an unanswered lookup is genuinely ambiguous rather than the
	 * default.
	 */
	if (activeGrants.length === 0) return null;

	const grant = activeGrants[0];
	// `=== "write"` — absence means read-only. See `useCompanySupportAccess`.
	const canWrite = grant.mode === "write";

	return (
		<div
			style={canWrite ? writeBarStyle : barStyle}
			role="status"
			data-testid="customer-support-access-bar"
			data-mode={canWrite ? "write" : "read"}
		>
			<strong>{supportAccessCopy.customer.liveTitle}</strong>
			<span style={{ opacity: 0.9 }}>
				{canWrite
					? supportAccessCopy.customer.liveDetailWrite
					: supportAccessCopy.customer.liveDetail}
			</span>
			<Popconfirm
				title={supportAccessCopy.customer.endConfirm}
				description={supportAccessCopy.customer.endConfirmDetail}
				okText={supportAccessCopy.customer.endAccess}
				onConfirm={async () => {
					await api.post(
						`companies/${companyId}/support-access/grants/${grant.id}/revoke`,
					);
					refresh();
				}}
			>
				<Button size="small" danger data-testid="customer-end-access">
					{supportAccessCopy.customer.endAccess}
				</Button>
			</Popconfirm>
		</div>
	);
}

/**
 * A pending request is a MODAL INTERRUPT, not a page someone has to find.
 *
 * Consent that waits on the customer navigating to a settings tab is consent
 * that mostly does not happen — which routes the ticket to break-glass, the
 * tier we least want carrying routine traffic.
 */
export function SupportAccessConsentModal() {
	const companyId = useCompanyStore((s) => s.id);
	const { pendingRequests, refresh } = useCompanySupportAccess(
		companyId ?? undefined,
	);
	const [busy, setBusy] = useState(false);
	// Deferring is allowed but is NOT an answer: the request stays open and the
	// prompt returns next load. Nothing here can mark it decided except the
	// customer choosing.
	const [deferred, setDeferred] = useState<string[]>([]);

	const request = pendingRequests.find((r) => !deferred.includes(r.id));
	if (!companyId || !request) return null;

	const answer = async (decision: "approve" | "deny") => {
		setBusy(true);
		try {
			await api.post(
				`companies/${companyId}/support-access/requests/${request.id}/${decision}`,
			);
			refresh();
		} finally {
			setBusy(false);
		}
	};

	return (
		<Modal
			open
			data-testid="support-access-consent-modal"
			title="Equility support is asking to access this account"
			okText="Allow access"
			cancelText="Deny"
			confirmLoading={busy}
			onOk={() => answer("approve")}
			onCancel={() => answer("deny")}
			// No dismiss-by-accident: a stray click outside must not deny a
			// legitimate request, and it must not leave it silently unanswered.
			maskClosable={false}
			closable={false}
			footer={(_, { OkBtn, CancelBtn }) => (
				<>
					<Button onClick={() => setDeferred((d) => [...d, request.id])}>
						Decide later
					</Button>
					<CancelBtn />
					<OkBtn />
				</>
			)}
		>
			<Typography.Paragraph>
				A member of Equility support has asked to view this account as one of
				your users, to help with a support request.
			</Typography.Paragraph>
			<Typography.Paragraph data-testid="consent-reason">
				<strong>Their reason:</strong> {request.reason}
			</Typography.Paragraph>
			{request.ticketRef && (
				<Typography.Paragraph>
					<strong>Ticket:</strong> {request.ticketRef}
				</Typography.Paragraph>
			)}
			<Alert
				type="info"
				showIcon
				message="This access is read-only and time-limited."
				description={`If you allow it, support can view this account for up to ${request.requestedDurationMinutes} minutes and cannot change anything. You can end the session at any time.`}
			/>
		</Modal>
	);
}

const barStyle: React.CSSProperties = {
	display: "flex",
	alignItems: "center",
	gap: 12,
	padding: "8px 16px",
	fontSize: 13,
	background: "#b45309",
	color: "white",
	// Same reasoning as the staff bar: above AntD's Modal/Drawer (1000) and
	// notifications (1010).
	zIndex: 1100,
	position: "sticky",
	top: 0,
	flexWrap: "wrap",
};

/**
 * A session that can CHANGE the customer's books is a different statement about
 * their account, so it gets a different colour rather than a longer sentence in
 * the same one.
 */
const writeBarStyle: React.CSSProperties = {
	...barStyle,
	background: "#b91c1c",
};
